//src/pages/inventory/Product/EditMedicineModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import BasicModal from "../../../components/BasicModal";
import FormModalActions from "../../../components/FormModalActions";
import {
  DENSE_FIELD_PROPS,
  DENSE_FORM_BODY_SX,
  FORM_MODAL_CONTENT_SX,
} from "../../../utils/formModalStyles";
import { updateMedicine, getAllMedicine } from "../../../services/medicineNewService";
import { updateInventoryRecordsByMedicineId } from "../../../services/medicineInventoryService";
import { useSelector } from "react-redux";
import {
  buildMedicineSavePayload,
  filterMedicineTypesForPackage,
  getCostPreviewLabel,
  getDetailsFieldLabel,
  isBoxOrSyrupMedicine,
  isCapsuleOrTablet,
  isFluidMedicine,
  MEDICINE_DETAILS_OPTIONS,
  usesDetailsMultiplier,
} from "../../../utils/medicinePricingUtils";
import {
  getMedicineDisplayName,
  validateUniqueCatalogName,
} from "../../../utils/duplicateNameUtils";
import { useSubscriptionCapabilities } from "../../../hooks/useSubscriptionCapabilities";
import { handleFormattedNameBlur } from "../../../utils/nameFormatUtils";

const editValidationSchema = Yup.object({
  medicineName: Yup.string()
    .required("Medicine name is required")
    .min(2, "Medicine name should be at least 2 characters long"),
  medicineType: Yup.string().required("Medicine type is required"),
  manufacturer: Yup.string().required("Manufacturer is required"),
  price: Yup.number()
    .required("Price is required")
    .min(0, "Price must be at least 0")
    .positive("Price must be a positive number"),
  medicineDetails: Yup.string()
    .oneOf(MEDICINE_DETAILS_OPTIONS, "Invalid medicine details")
    .required("Medicine details are required"),
});

const detailsMultiplierValidationSchema = Yup.object({
  details: Yup.number()
    .required("This field is required")
    .min(1, "Value must be at least 1")
    .positive("Value must be a positive number"),
});

const boxSyrupValidationSchema = Yup.object({
  details: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) {
        return 1;
      }
      return value;
    })
    .min(1, "Details must be at least 1"),
});

const EditMedicineModal = ({ open, onClose, medicineData }) => {
  const user = useSelector((state) => state.user.user);
  const { isPremiumPackage } = useSubscriptionCapabilities();
  const [existingMedicines, setExistingMedicines] = useState([]);

  const visibleMedicineTypes = useMemo(
    () =>
      filterMedicineTypesForPackage(undefined, {
        isPremium: isPremiumPackage,
        keepValue: medicineData?.medicineType,
      }),
    [isPremiumPackage, medicineData?.medicineType]
  );

  useEffect(() => {
    if (!open) return;
    const loadMedicines = async () => {
      try {
        const data = await getAllMedicine();
        setExistingMedicines(Array.isArray(data) ? data : []);
      } catch (error) {
        console.warn("[EditMedicineModal] Could not load medicines for duplicate check:", error);
        setExistingMedicines([]);
      }
    };
    loadMedicines();
  }, [open]);

  const formik = useFormik({
    initialValues: {
      medicineName: medicineData?.name || "",
      medicineType: medicineData?.medicineType || "",
      manufacturer: medicineData?.manufacturer || "",
      medicineDetails: medicineData?.medicineDetails || "",
      details: medicineData?.details ?? "",
      price: medicineData?.price ?? "",
      userId: user?.id || 1,
    },
    enableReinitialize: true,
    validationSchema: editValidationSchema,
    validate: (values) => {
      const errors = {};
      const duplicateError = validateUniqueCatalogName({
        name: values.medicineName,
        items: existingMedicines,
        excludeId: medicineData?.id,
        getName: getMedicineDisplayName,
        getId: (item) => item?.id,
        entityLabel: "Medicine",
      });
      if (duplicateError) {
        errors.medicineName = duplicateError;
      }
      return errors;
    },
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setFieldError, setFieldTouched }) => {
      try {
        const schema = usesDetailsMultiplier(values.medicineDetails)
          ? detailsMultiplierValidationSchema
          : boxSyrupValidationSchema;

        await schema.validate(values, { abortEarly: false });

        let medicines = existingMedicines;
        try {
          const fresh = await getAllMedicine();
          medicines = Array.isArray(fresh) ? fresh : [];
          setExistingMedicines(medicines);
        } catch (error) {
          console.warn("[EditMedicineModal] Could not refresh medicines for duplicate check:", error);
        }

        const duplicateError = validateUniqueCatalogName({
          name: values.medicineName,
          items: medicines,
          excludeId: medicineData?.id,
          getName: getMedicineDisplayName,
          getId: (item) => item?.id,
          entityLabel: "Medicine",
        });
        if (duplicateError) {
          setFieldTouched("medicineName", true, false);
          setFieldError("medicineName", duplicateError);
          return;
        }

        const req_data = {
          id: medicineData.id,
          ...buildMedicineSavePayload(values, user?.id || 1),
        };

        await updateMedicine(req_data);

        const inventoryUpdateData = {
          name: values.medicineName,
          medicineType: values.medicineType,
          manufacturer: values.manufacturer,
          userId: user?.id || 1,
        };

        await updateInventoryRecordsByMedicineId(medicineData.id, inventoryUpdateData);

        onClose();
      } catch (error) {
        const message =
          error?.response?.data?.message || error?.message || "";
        if (/already exists|duplicate/i.test(message)) {
          setFieldTouched("medicineName", true, false);
          setFieldError("medicineName", message);
          return;
        }
        console.error("Error updating medicine:", error);
        alert(message || "Failed to update medicine. Please try again.");
      }
    },
  });

  if (!open) return null;

  const costPreview = getCostPreviewLabel(
    formik.values.medicineDetails,
    formik.values.price,
    formik.values.details
  );

  return (
    <BasicModal open={open} onClose={onClose} title="Edit Medicine">
      <Box sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="medicineName"
                name="medicineName"
                label="Medicine Name"
                value={formik.values.medicineName}
                onChange={formik.handleChange}
                onBlur={(e) =>
                  handleFormattedNameBlur(e, {
                    setFieldValue: formik.setFieldValue,
                    handleBlur: formik.handleBlur,
                  })
                }
                error={formik.touched.medicineName && Boolean(formik.errors.medicineName)}
                helperText={formik.touched.medicineName && formik.errors.medicineName}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel id="edit_medicine_type-label">Medicine Type</InputLabel>
                <Select
                  labelId="edit_medicine_type-label"
                  id="edit_medicine_type"
                  name="medicineType"
                  label="Medicine Type"
                  value={formik.values.medicineType}
                  onChange={(event) => {
                    formik.setFieldValue("medicineType", event.target.value);
                  }}
                  error={formik.touched.medicineType && Boolean(formik.errors.medicineType)}
                >
                  <MenuItem value="">Select medicine type</MenuItem>
                  {visibleMedicineTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.medicineType && formik.errors.medicineType && (
                  <Typography color="error" variant="caption">
                    {formik.errors.medicineType}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="manufacturer"
                name="manufacturer"
                label="Manufacturer"
                value={formik.values.manufacturer}
                onChange={formik.handleChange}
                onBlur={(e) =>
                  handleFormattedNameBlur(e, {
                    setFieldValue: formik.setFieldValue,
                    handleBlur: formik.handleBlur,
                  })
                }
                error={formik.touched.manufacturer && Boolean(formik.errors.manufacturer)}
                helperText={formik.touched.manufacturer && formik.errors.manufacturer}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel id="edit_medicine_details-label">Medicine Details</InputLabel>
                <Select
                  labelId="edit_medicine_details-label"
                  id="edit_medicineDetails"
                  name="medicineDetails"
                  label="Medicine Details"
                  value={formik.values.medicineDetails}
                  onChange={(event) => {
                    const selectedType = event.target.value;
                    formik.setFieldValue("medicineDetails", selectedType);
                    if (isBoxOrSyrupMedicine(selectedType)) {
                      formik.setFieldValue("details", 1);
                    } else if (!usesDetailsMultiplier(selectedType)) {
                      formik.setFieldValue("details", "");
                    }
                  }}
                  error={formik.touched.medicineDetails && Boolean(formik.errors.medicineDetails)}
                >
                  <MenuItem value="">Select type</MenuItem>
                  {MEDICINE_DETAILS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.medicineDetails && formik.errors.medicineDetails && (
                  <Typography color="error" variant="caption">
                    {formik.errors.medicineDetails}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {(isCapsuleOrTablet(formik.values.medicineDetails) ||
              isFluidMedicine(formik.values.medicineDetails)) && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  id="details"
                  name="details"
                  label={getDetailsFieldLabel(formik.values.medicineDetails)}
                  value={formik.values.details}
                  onChange={formik.handleChange}
                  error={formik.touched.details && Boolean(formik.errors.details)}
                  helperText={
                    (formik.touched.details && formik.errors.details) ||
                    (isFluidMedicine(formik.values.medicineDetails)
                      ? "Example: 600 drops/bottle at ₹52.5 → ₹0.0875 per drop"
                      : "")
                  }
                  inputProps={{ min: 1 }}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                id="price"
                name="price"
                type="number"
                label="Cost/Price"
                value={formik.values.price}
                onChange={formik.handleChange}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={
                  (formik.touched.price && formik.errors.price) || costPreview || undefined
                }
                inputProps={{ min: 0, step: "0.01" }}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item xs={12}>
              <FormModalActions
                onCancel={onClose}
                submitType="submit"
                submitLabel="Save"
                submitDisabled={formik.isSubmitting}
                loading={formik.isSubmitting}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default EditMedicineModal;
